import React from 'react';
import { css } from '@emotion/css';
import ModalLayout from '../../../../../components/structure/ModalLayout';
import ModalTitle from '../../../../../components/general/ModalTitle';
import Button from '../../../../../components/general/Button';
import HorizontalContainer from '../../../../../components/structure/HorizontalContainer';
import CustomScrollbars from '../../../../../components/structure/CustomScrollbars';
import { color_shades_dark, color_shades_lighter, dark_grey_blue_70 } from '../../../../../constants/colors';
import FooterBar from '../../../../../components/structure/FooterBar';

const closeIcon = require('../../../../../content/images/close_icon.svg');

interface ZohoInstructionsModalProps {
    onClose: () => void;
}

const ZohoInstructionsModal: React.FC<ZohoInstructionsModalProps> = ({ onClose }) => {
    return (
        <ModalLayout isVisible={true} onClose={onClose}>
            <div className={containerStyle}>
                <HorizontalContainer className={modalTitleContainer}>
                    <ModalTitle>Zoho CRM Integration Setup Guide</ModalTitle>
                    <Button themes={['end-tight', 'icon']} icon={closeIcon} onClick={onClose} />
                </HorizontalContainer>
                <CustomScrollbars autoHide>
                    <div className={contentStyle}>
                        <section>
                            <h3>Step 1: Access Zoho API Console</h3>
                            <ol>
                                <li>Go to <a href="https://api-console.zoho.com/" target="_blank" rel="noopener noreferrer">https://api-console.zoho.com/</a></li>
                                <li>If prompted, log in to your Zoho account</li>
                            </ol>
                        </section>

                        <section>
                            <h3>Step 2: Create a New Client</h3>
                            <ol>
                                <li>Click on "Add Client"</li>
                                <li>Select "Server-based Applications"</li>
                            </ol>
                        </section>

                        <section>
                            <h3>Step 3: Configure the Application</h3>
                            <ol>
                                <li>Fill out the form with the following details:
                                    <ul>
                                        <li>Client Name: Voicify</li>
                                        <li>Homepage URL: <a href="https://voicify.com" target="_blank" rel="noopener noreferrer">https://voicify.com</a></li>
                                        <li>Authorized Redirect URIs:
                                            <ul>
                                                <li><a href="https://spark.voicify.com/v/orgSettings" target="_blank" rel="noopener noreferrer">https://spark.voicify.com/v/orgSettings</a></li>
                                                <li><a href="https://dev-spark.voicify.com/v/orgSettings" target="_blank" rel="noopener noreferrer">https://dev-spark.voicify.com/v/orgSettings</a></li>
                                            </ul>
                                        </li>
                                    </ul>
                                </li>
                                <li>Click "Create"</li>
                            </ol>
                        </section>

                        <section>
                            <h3>Step 4: Get Credentials</h3>
                            <ol>
                                <li>After the app is created, you will be shown the Client ID and Client Secret</li>
                                <li>Copy the Client ID</li>
                                <li>Copy the Client Secret</li>
                            </ol>
                        </section>

                        <section>
                            <h3>Step 5: Complete Integration in Spark</h3>
                            <ol>
                                <li>Return to the Spark integration settings</li>
                                <li>Paste the copied Client ID into the "Client ID" field</li>
                                <li>Paste the copied Client Secret into the "Client Secret" field</li>
                                <li>Click "Authenticate" to complete the setup</li>
                            </ol>
                        </section>
                    </div>
                </CustomScrollbars>
                <FooterBar>
                    <Button themes={["primary", "wide"]} text="Close" onClick={onClose} />
                </FooterBar>
            </div>
        </ModalLayout>
    );
};

const containerStyle = css`
    display: flex;
    flex-direction: column;
    height: 90vh;
    width: 100%;
`;

const modalTitleContainer = css`
    padding: 24px;
    border-bottom: 1px solid ${color_shades_dark};
`;

const contentStyle = css`
    padding: 24px;
    padding-bottom: 120px;
    section {
        margin-bottom: 32px;

        &:last-child {
            margin-bottom: 0;
        }

        h3 {
            font-size: 18px;
            font-weight: 600;
            margin-bottom: 16px;
            color: #000;
        }

        p, a {
            font-size: 14px;
            line-height: 1.5;
            color: ${dark_grey_blue_70};
            margin-bottom: 12px;
        }

        a {
            color: #0066cc; /* Link color */
            text-decoration: underline;
        }

        ul, ol {
            margin-left: 24px;
            margin-bottom: 16px;

            li {
                font-size: 14px;
                line-height: 1.2;
                color: ${dark_grey_blue_70};
                margin-bottom: 8px;

                ul, ol {
                    margin-top: 8px;
                }
            }
        }
    }
`;

export default ZohoInstructionsModal; 